import { ChevronDown, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { useDeleteSavedDashboard } from "../../api/hooks/useDeleteSavedDashboard";
import { useSavedDashboards } from "../../api/hooks/useSavedDashboards";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { readLayoutFromBlob } from "./widgets/layoutAdapter";
import {
  DEFAULT_DASHBOARD_LAYOUT,
  type DashboardLayout,
} from "./widgets/types";

// Sentinel id used by the picker to represent the built-in default
// layout. Real SavedDashboard rows carry server-issued UUIDs and never
// collide with the all-zeros pattern.
export const DEFAULT_DASHBOARD_ID = "00000000-0000-0000-0000-000000000000";
// Renamed from "Dashboard (default)" to "Default layout" so the sentinel
// label never collides with a user-named SavedDashboard row.
export const DEFAULT_DASHBOARD_NAME = "Default layout";

type PendingDelete = { id: string; name: string };

export type CustomDashboardSelectProps = {
  selectedId: string;
  onSelect: (id: string, layout: DashboardLayout) => void;
};

export function CustomDashboardSelect({
  selectedId,
  onSelect,
}: CustomDashboardSelectProps) {
  const { data, isLoading, isError } = useSavedDashboards();
  const deleteMutation = useDeleteSavedDashboard();
  const [open, setOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(event: MouseEvent) {
      if (
        wrapperRef.current &&
        event.target instanceof Node &&
        !wrapperRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    }
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  // The backend prepends a synthetic "Dashboard (default)" entry
  // carrying the sentinel UUID on every list response. Phase 5 added a
  // client-side sentinel ("Default layout"); drop the server one by id
  // so the menu shows only the frontend label, not both.
  const items = (data?.items ?? []).filter(
    (i) => !i.is_deleted && i.saved_dashboard_id !== DEFAULT_DASHBOARD_ID,
  );
  const active = items.find((i) => i.saved_dashboard_id === selectedId);
  const label =
    selectedId === DEFAULT_DASHBOARD_ID || active === undefined
      ? DEFAULT_DASHBOARD_NAME
      : active.name;

  function pickDefault() {
    onSelect(DEFAULT_DASHBOARD_ID, DEFAULT_DASHBOARD_LAYOUT);
    setOpen(false);
  }

  function pickSaved(id: string, blob: { [key: string]: unknown }) {
    onSelect(id, readLayoutFromBlob(blob));
    setOpen(false);
  }

  async function confirmDelete() {
    if (pendingDelete === null) return;
    const id = pendingDelete.id;
    try {
      await deleteMutation.mutateAsync(id);
      // If the deleted dashboard was the active one, fall back to the
      // built-in default so the page never holds a stale id.
      if (selectedId === id) {
        onSelect(DEFAULT_DASHBOARD_ID, DEFAULT_DASHBOARD_LAYOUT);
      }
      setPendingDelete(null);
    } catch {
      // Leave the dialog open so the operator can retry.
    }
  }

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Custom dashboard selector"
        onClick={() => {
          setOpen((prev) => !prev);
        }}
        className="inline-flex items-center gap-1 rounded-control border border-outline-variant bg-surface px-3 py-1.5 text-sm font-medium text-on-surface transition-colors hover:bg-surface-container-high"
      >
        <span className="max-w-[180px] truncate">{label}</span>
        <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
      {open && (
        <ul
          role="menu"
          className="absolute left-0 top-full z-30 mt-1 min-w-[240px] max-h-72 overflow-auto rounded-md border border-outline-variant bg-surface-container py-1 shadow-lg"
        >
          <li role="none">
            <button
              type="button"
              role="menuitem"
              onClick={pickDefault}
              className={`w-full truncate px-3 py-1.5 text-left text-sm hover:bg-surface-container-high ${
                selectedId === DEFAULT_DASHBOARD_ID
                  ? "bg-surface-container-high font-medium"
                  : ""
              }`}
            >
              {DEFAULT_DASHBOARD_NAME}
            </button>
          </li>
          {isLoading && (
            <li role="none" className="px-3 py-1.5 text-sm text-on-surface-muted">
              Loading…
            </li>
          )}
          {isError && (
            <li role="none" className="px-3 py-1.5 text-sm text-danger">
              Failed to load dashboards
            </li>
          )}
          {items.map((item) => (
            <li
              key={item.saved_dashboard_id}
              role="none"
              className={`group flex items-center justify-between gap-1 hover:bg-surface-container-high ${
                selectedId === item.saved_dashboard_id
                  ? "bg-surface-container-high"
                  : ""
              }`}
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  pickSaved(item.saved_dashboard_id, item.layout);
                }}
                className={`min-w-0 flex-1 truncate px-3 py-1.5 text-left text-sm ${
                  selectedId === item.saved_dashboard_id ? "font-medium" : ""
                }`}
              >
                {item.name}
              </button>
              <button
                type="button"
                aria-label={`Delete ${item.name}`}
                onClick={(event) => {
                  event.stopPropagation();
                  setPendingDelete({
                    id: item.saved_dashboard_id,
                    name: item.name,
                  });
                }}
                className="mr-1 shrink-0 rounded p-1 text-on-surface-muted opacity-0 transition-opacity hover:text-danger focus-visible:opacity-100 group-hover:opacity-100"
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(next) => {
          if (!next && !deleteMutation.isPending) setPendingDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete saved dashboard?</AlertDialogTitle>
            <AlertDialogDescription>
              Delete &lsquo;{pendingDelete?.name ?? ""}&rsquo;? The default
              layout is unaffected and stays available.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteMutation.isError && (
            <p role="alert" className="text-sm text-danger">
              Failed to delete. Try again.
            </p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteMutation.isPending}
              onClick={(event) => {
                event.preventDefault();
                void confirmDelete();
              }}
              className="bg-danger text-on-danger hover:bg-danger/90"
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
